import useCustomerQueryCaa from "@repo/shopify/queries/useCustomerQueryCaa";
import { discoverCaaEndpoints } from "@repo/shopify/utils/caaDiscovery";
import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import Button from "@atoms/Button";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";
import { SHOPIFY_DOMAIN, URLS } from "@const/urls";

import useCustomerQuery from "@hooks/useCustomerQuery";
import useLanguage from "@hooks/useLanguage";

import apolloCaaClient from "@lib/apolloCaa";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

const LINKS = [
  { href: URLS.ORDER_HISTORY, label: "Order History" },
  { href: URLS.ACCOUNT, label: "Account" },
  { href: URLS.ADDRESSES, label: "Addresses" },
];

export default function Account() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { lang } = useLanguage();

  const { fetchCustomer } = useCustomerQuery();
  const { fetchCaaCustomer } = useCustomerQueryCaa({ client: apolloCaaClient });
  const { customer, caaToken, logout, token, updateCaa } = useCustomerStore();
  const { openToast, setMeta, openAccount } = useLayoutStore();

  /** 계정 내 전체 주문 라인아이템 개수 (환불 상품 제외) */
  const [sessionChecked, setSessionChecked] = useState(false);

  const hasSession = lang === "jp" ? Boolean(caaToken?.accessToken) : Boolean(token.accessToken);

  // 로그아웃된 상황에서 접근했을 때
  useEffect(() => {
    if (!hasSession) {
      navigate(URLS.HOME);
      setTimeout(() => {
        openAccount(true);
      }, 500);
    }

    window.scrollTo(0, 0);
  }, []);

  // 세션이 바뀔 때만 검증
  useEffect(() => {
    setSessionChecked(false);
  }, [hasSession]);

  // 세션이 만료되었을때
  useEffect(() => {
    // 조회 실패(리프레시 실패 등) 또는 customer가 없는 경우 — Loading에 갇히지 않도록 명시적으로 로그아웃 처리
    const handleSessionExpired = () => {
      openToast(NOTIFICATIONS.WARNING_END_SESSION);
      logout();
      navigate(URLS.HOME);
      setTimeout(() => {
        openAccount(true);
      }, 300);
    };

    if (lang === "jp") {
      if (!caaToken?.accessToken) return;
      void fetchCaaCustomer()
        .then(({ data }) => {
          if (data?.customer) {
            updateCaa(data.customer);
            setSessionChecked(true);
          } else {
            handleSessionExpired();
          }
        })
        .catch(handleSessionExpired);
      return;
    }

    void fetchCustomer({ variables: { customerAccessToken: token.accessToken } })
      .then(({ data }) => {
        if (data?.customer) setSessionChecked(true);
        else handleSessionExpired();
      })
      .catch(handleSessionExpired);
  }, [token, caaToken, pathname]);

  useEffect(() => {
    setMeta({ title: `Account - ${DEFAULT_TITLE}` });
  }, []);

  const handleLogout = async () => {
    // jp(CAA)는 accounts.shopify.com에 별도 세션이 있으므로, 로컬 상태만 지우면 실제로는 로그아웃되지 않는다.
    // end_session_endpoint로 리다이렉트해 IdP 세션까지 끝낸다.
    const idTokenHint = caaToken?.idToken;

    logout();
    openToast(NOTIFICATIONS.SUCCESS_LOGOUT);

    if (lang === "jp" && idTokenHint) {
      try {
        const jpShopDomain = SHOPIFY_DOMAIN.jp.replace(/^https?:\/\//, "").replace(/\/$/, "");
        const { endSessionEndpoint } = await discoverCaaEndpoints(jpShopDomain);

        const params = new URLSearchParams({
          id_token_hint: idTokenHint,
          post_logout_redirect_uri: `${window.location.origin}${URLS.HOME}`,
        });

        window.location.href = `${endSessionEndpoint}?${params.toString()}`;
        return;
      } catch {
        // discovery 실패해도 로컬 로그아웃은 이미 처리됐으므로 SPA 라우팅으로 폴백
      }
    }

    navigate(URLS.HOME);
  };

  return (
    <>
      <section className="pc:col-span-12 col-span-8 flex gap-2.5">
        {LINKS.map(({ href, label }) => {
          if (customer.tags.includes("PARTNERSHIP_TOSS") && label === "Addresses") return;
          return (
            <Link as="text" href={href} isActive={pathname === href} key={href}>
              {label}
            </Link>
          );
        })}
        <Button isActive={false} onClick={handleLogout}>
          Logout
        </Button>
      </section>
      {sessionChecked ? <Outlet /> : <Loading type="page" />}
    </>
  );
}
